import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { SGSForm, SGSFormDate, SGSFormInput, SGSFormLayout, SGSRadioCtr } from 'components';
import SGSInputCurrency from 'components/SGSCurrency';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { InterimStatus, interimStatusOptions } from 'features/arretTravail/models/interim';
import { ouiNonOptions, TrueFalse } from 'features/arretTravail/models/StopInformation/workStoppage';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import styles from '../../../css/ArretTravail.module.scss';

type Props = {
    store: any;
    onSubmit: SubmitHandler<InterimSchema>;
    actions?: JSX.Element;
};

type InterimSchema = {
    interim: {
        isTravelAccident: string;
        statut: InterimStatus;
        dateCdi?: Date;
        flexibilityPeriod?: {
            isFlexible: string;
            startDate?: Date;
            endDate?: Date;
        };
        mission?: {
            startDate?: Date;
            endDate?: Date;
        };
        weeklyDuration?: string;
        hourlyRate?: string;
        grossHourlyBonus?: string;
        payrollRate?: string;
        pasRate?: string;
    };
};

const FormInterim: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const { t } = useTranslation();
    const { activeStep, setStepFailed } = useArretTravailFormContext();

    const validationSchema: Yup.SchemaOf<InterimSchema> = Yup.object({
        interim: Yup.object({
            isTravelAccident: Yup.string().required(t('yup.validators.required')),
            statut: Yup.mixed<InterimStatus>().oneOf(Object.values(InterimStatus)).required(t('yup.validators.required')),
            dateCdi: Yup.date()
                .when('statut', {
                    is: InterimStatus.Cdii,
                    then: Yup.date().required(t('yup.validators.required')),
                    otherwise: Yup.date().nullable(),
                })
                .nullable(),
            flexibilityPeriod: Yup.object({
                isFlexible: Yup.string().required(t('yup.validators.required')),
                startDate: Yup.date().nullable(),
                endDate: Yup.date().nullable(),
            })
                .test('flexibilityPeriod-dates', t('yup.validators.required'), function (value) {
                    const { isFlexible, startDate } = value || {};

                    if (isFlexible === TrueFalse.True) {
                        if (!startDate) {
                            return this.createError({
                                path: 'interim.flexibilityPeriod.startDate',
                                message: t('yup.validators.required'),
                            });
                        }
                    }
                    return true;
                })
                .test('flexibilityPeriod-dates', t('yup.validators.required'), function (value) {
                    const { isFlexible, endDate } = value || {};

                    if (isFlexible === TrueFalse.True) {
                        if (!endDate) {
                            return this.createError({
                                path: 'interim.flexibilityPeriod.endDate',
                                message: t('yup.validators.required'),
                            });
                        }
                    }
                    return true;
                }),
            mission: Yup.object({
                startDate: Yup.date().nullable(),
                endDate: Yup.date().nullable(),
            }).when('statut', {
                is: InterimStatus.Ctt,
                then: Yup.object({
                    startDate: Yup.date().required(t('yup.validators.required')),
                    endDate: Yup.date().required(t('yup.validators.required')),
                }),
                otherwise: Yup.object({
                    startDate: Yup.date().nullable(),
                    endDate: Yup.date().nullable(),
                }),
            }),
            weeklyDuration: Yup.string().required(t('yup.validators.required')),
            hourlyRate: Yup.string().required(t('yup.validators.required')),
            grossHourlyBonus: Yup.string().required(t('yup.validators.required')),
            payrollRate: Yup.string().required(t('yup.validators.required')),
            pasRate: Yup.string().required(t('yup.validators.required')),
        }),
    });

    const methods = useForm<InterimSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: store,
    });

    const {
        handleSubmit,
        watch,
        formState: { errors },
        control,
        setValue,
    } = methods;

    const statutInterimaire = watch('interim.statut');
    const isCdii = statutInterimaire === InterimStatus.Cdii;
    const isCtt = statutInterimaire === InterimStatus.Ctt;

    const isFlexibilityPeriod = watch('interim.flexibilityPeriod.isFlexible');
    const hasFlexibilityPeriod = isFlexibilityPeriod === TrueFalse.True;

    useEffect(() => {
        if (statutInterimaire === InterimStatus.Ctt) {
            setValue('interim.dateCdi', null);
        }
    }, [statutInterimaire, setValue]);

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    console.log(errors);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <Box className={styles.title}>Accident de trajet</Box>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <SGSRadioCtr row options={ouiNonOptions} name="interim.isTravelAccident" className="mb-n3" label="* S'agit-il d'un accident de trajet ?" />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <Box className={styles.title}>Statut de l'intérimaire</Box>
                        <SGSForm.Row>
                            <SGSForm.Col>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <SGSRadioCtr row options={interimStatusOptions} name="interim.statut" className="mb-n3" label="* Statut de l’intérimaire" />
                                    </Grid>
                                    {isCdii && (
                                        <Grid item xs={12} md={6}>
                                            <Box className={'mt-2'}>
                                                <SGSFormDate inputVariant="outlined" name="interim.dateCdi" label="Date de début du CDI" />
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        {isCtt && (
                            <>
                                <Box className={styles.title}>Mission</Box>
                                <SGSForm.Row>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="interim.mission.startDate" label="Date de début de la mission" />
                                    </SGSForm.Col>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="interim.mission.endDate" label="Date de fin de la mission" />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                            </>
                        )}
                        <Box className={styles.title}>Période de souplesse</Box>
                        <SGSForm.Row>
                            <SGSForm.Col md={12}>
                                <SGSRadioCtr
                                    row
                                    options={ouiNonOptions}
                                    name="interim.flexibilityPeriod.isFlexible"
                                    className="mb-n3"
                                    label="* Est-ce que l'arrêt de travail survient dans une période de souplesse ?"
                                />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        {hasFlexibilityPeriod && (
                            <>
                                <SGSForm.Row>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="interim.flexibilityPeriod.startDate" label="Date de début de la souplesse" />
                                    </SGSForm.Col>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="interim.flexibilityPeriod.endDate" label="Date de fin de la souplesse" />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                            </>
                        )}
                        <Box className={styles.title}>Informations sur la rémunération</Box>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <SGSFormInput variant="outlined" name="interim.weeklyDuration" label="* Durée hebdomadaire" />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <SGSInputCurrency name="interim.hourlyRate" label="* Taux horaire brut" />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <SGSInputCurrency name="interim.grossHourlyBonus" label="* Prime horaire brute" />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <SGSFormInput variant="outlined" name="interim.payrollRate" label="* Taux de charge salariale" />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <SGSFormInput variant="outlined" name="interim.pasRate" label="* Taux PAS" />
                            </SGSForm.Col>
                        </SGSForm.Row>
                    </Grid>
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormInterim;
